export interface PriceCatalogItem {
  id: number | string;
  [key: string]: unknown;
}

export interface PriceCatalogState {
  listLoading: boolean;
  pageCount: number;
  currentPageNumber: number;
  entities: PriceCatalogItem[] | null;
  totalResult: number;
  error: string | null;
}

export interface PriceCatalogPayload {
  totalPageCount: number;
  currentPageNumber: number;
  list: PriceCatalogItem[];
  totalResultCount: number;
}

const defaultState: PriceCatalogState = {
  listLoading: false,
  pageCount: 0,
  currentPageNumber: 0,
  entities: null,
  totalResult: 0,
  error: null,
};

export const actionTypes = {
  priceCatalogFetched: 'PRICE_CATALOG_FETCHED',
  priceCatalogAppended: 'PRICE_CATALOG_APPENDED',
  addItem: 'ADD_ITEM',
  editItem: 'EDIT_ITEM',
  deleteItem: 'DELETE_ITEM',
  catchError: 'CATCH_ERROR',
} as const;

type PriceCatalogFetchedAction = {
  type: typeof actionTypes.priceCatalogFetched;
  payload: PriceCatalogPayload;
};

type PriceCatalogAppendedAction = {
  type: typeof actionTypes.priceCatalogAppended;
  payload: PriceCatalogPayload;
};

type PriceCatalogAddItemAction = {
  type: typeof actionTypes.addItem;
  payload: PriceCatalogItem;
};

type PriceCatalogEditItemAction = {
  type: typeof actionTypes.editItem;
  payload: PriceCatalogItem;
};

type PriceCatalogDeleteItemAction = {
  type: typeof actionTypes.deleteItem;
  payload: {id: number};
};

type CatchErrorAction = {
  type: typeof actionTypes.catchError;
  payload: { error: string };
};

type PriceCatalogAction =
  | PriceCatalogFetchedAction
  | PriceCatalogAppendedAction
  | PriceCatalogAddItemAction
  | PriceCatalogEditItemAction
  | PriceCatalogDeleteItemAction
  | CatchErrorAction;

const getIndex = (array: PriceCatalogItem[], id: number | string) => {
  const mappedArray = array.map(item => item.id);
  return mappedArray.indexOf(id);
};

export const priceCatalogTableReducer = (state: PriceCatalogState = defaultState, action: PriceCatalogAction): PriceCatalogState => {
  switch (action.type) {
    case actionTypes.priceCatalogFetched: {
      const { totalPageCount, currentPageNumber, list, totalResultCount } = action.payload;
      return {
        ...state,
        listLoading: false,
        pageCount: totalPageCount,
        currentPageNumber,
        entities: list,
        totalResult: totalResultCount,
        error: null,
      };
    }
    case actionTypes.priceCatalogAppended: {
      const { totalPageCount, currentPageNumber, list, totalResultCount } = action.payload;
      return {
        ...state,
        listLoading: false,
        pageCount: totalPageCount,
        currentPageNumber,
        entities: state.entities
          ? [...state.entities, ...list]
          : [...list],
        totalResult: totalResultCount,
        error: null,
      };
    }
    case actionTypes.addItem: {
      if (Array.isArray(state.entities)) {
        state.entities.unshift({ ...action.payload });
      }
      return {
        ...state,
        entities: state.entities ? [...state.entities] : []
      }
    }
    case actionTypes.editItem: {
      if (Array.isArray(state.entities)) {
        const {id, ...rest} = action.payload;
        const index = getIndex(state.entities, id);
        state.entities[index] = {id, ...rest};
      }
      return {
        ...state,
        entities: state.entities ? [...state.entities] : []
      }
    }
    case actionTypes.deleteItem: {
      const {id} = action.payload;
      if (state.entities) {
        const index = getIndex(state.entities, id);
        state.entities.splice(index, 1);
      }
      return {
        ...state,
        entities: state.entities ? [...state.entities] : []
      }
    }
    case actionTypes.catchError: {
      const error = `PRICE_CATALOG: ${action.payload.error}`;
      return { ...state, error };
    }
    default:
      return state;
  }
};

export const actions = {
  priceCatalogFetched: (data: PriceCatalogPayload): PriceCatalogFetchedAction => ({
    type: actionTypes.priceCatalogFetched,
    payload: data,
  }),
  priceCatalogAppended: (data: PriceCatalogPayload): PriceCatalogAppendedAction => ({
    type: actionTypes.priceCatalogAppended,
    payload: data,
  }),
  addItem: (data: PriceCatalogItem) => ({
    type: actionTypes.addItem,
    payload: data,
  }),
  editItem: (data: PriceCatalogItem) => ({
    type: actionTypes.editItem,
    payload: data,
  }),
  deleteItem: (id: number): PriceCatalogDeleteItemAction => ({
    type: actionTypes.deleteItem,
    payload: {id},
  }),
  catchError: (error: string): CatchErrorAction => ({
    type: actionTypes.catchError,
    payload: {error},
  }),
};